/**
 * The drop-in splash screen (Cosmetic Progression Slice C, ADR-0044).
 *
 * One screen collects the **creation** identity - a display name, an optional
 * passcode (Lightweight Accounts Slice B), a sex (UO body 400 = male, 401 = female),
 * and a skin hue - and resolves them on submit so `main.ts` can open the
 * authoritative connection carrying them. Cosmetics are **no longer chosen here**:
 * characters start **naked** and earn their wardrobe from online time (PRD #101), so
 * the old loadout picker is gone and the live preview shows a **bare body** in the
 * chosen sex/skin. The passcode **reserves the typed name**: a non-blank value
 * claims it on first use and proves ownership on every later login, while a **blank
 * passcode reserves nothing** - an anonymous session with zero name uniqueness,
 * exactly the original drop-in behavior (ADR-0004, ADR-0039).
 *
 * This same form is the one-time "finish your character" step (ADR-0044): a legacy
 * account (a v1 reservation migrated without a body) supplies its sex + skin here on
 * its next login, and the server completes it **set-once**. A returning, already
 * completed account re-shows these fields but the server ignores them and spawns the
 * owner in their persisted body + outfit - identity is immutable at the store, not
 * merely by hiding the controls.
 *
 * This module owns only the DOM form wiring; the markup and styling live in
 * `index.html`. It reads no Pixi and no socket - it returns the chosen
 * {@link SplashIdentity} and reports every selection change through an
 * `onChange` hook so `main.ts` can drive the live preview - keeping the splash
 * decoupled from the game it precedes.
 */
import { SKIN_SWATCHES, type SkinSwatch } from './sim/skinTones';

/** The identity the splash collects, as the connect handshake needs it. */
export interface SplashIdentity {
  /** The chosen display name (may be empty; the server falls back to a default). */
  name: string;
  /** The chosen UO body id: 400 (male) or 401 (female). */
  sex: number;
  /** The chosen 1-based UO skin hue id applied to the bare body. */
  hue: number;
  /** Whether the one-time creation controls were shown and should be submitted. */
  appearance: boolean;
  /**
   * The passcode that reserves the typed name (Lightweight Accounts Slice B).
   * **Blank means "don't reserve"** - an anonymous, unreserved session (today's
   * behavior); a non-blank value claims the name on first use and proves ownership
   * on every later login. It rides the first `Login` **message**, never the connect
   * URL, so it stays out of logs and history (ADR-0038/ADR-0039).
   */
  passcode: string;
}

/** Optional hooks the host (`main.ts`) wires in - notably the live preview. */
export interface SplashHooks {
  /**
   * Called with the current {@link SplashIdentity} once at setup and on every
   * subsequent selection change, so the host can re-render the live preview.
   */
  onChange?: (identity: SplashIdentity) => void;
  /**
   * Called with the chosen {@link SplashIdentity} on every submit **after the
   * first** (the first submit resolves {@link runSplash}'s promise instead). The
   * host uses this to retry a connection after a {@link setSplashError} - e.g.
   * when a login was rejected because the name was taken (Lightweight Accounts
   * Slice A), the visitor edits the name and clicks Enter again, and this fires.
   */
  onResubmit?: (identity: SplashIdentity) => void;
}

/** One sex option the toggle offers: a UO body id plus its display label. */
interface SexOption {
  /** UO body id: 400 (male) or 401 (female). */
  value: number;
  /** The label shown on the toggle half. */
  label: string;
}

/**
 * The two body options the sex toggle offers, in display order. The first (male,
 * UO 400) is the pre-selected default, matching the historical splash default.
 */
const SEX_OPTIONS: readonly SexOption[] = [
  { value: 400, label: 'Male' },
  { value: 401, label: 'Female' },
];

// The skin-tone swatch palette ({@link SKIN_SWATCHES}) is the server-authoritative
// creation set, so it lives in the shared sim module `src/sim/skinTones.ts` (imported
// above) - one source of truth the splash and `server/identity.ts` both consume, so
// the client can never offer a tone the server would reject (ADR-0188).

/**
 * Wire the splash form in the given document and resolve with the chosen
 * {@link SplashIdentity} once the visitor submits. The splash is hidden on submit.
 * The form is built once; this never rejects (the visitor always picks eventually),
 * and unselected fields fall back to sensible defaults. `hooks.onChange` fires once
 * after setup and on every selection change so the host can drive a live preview of
 * the bare body in the chosen sex/skin.
 */
export function runSplash(doc: Document = document, hooks: SplashHooks = {}): Promise<SplashIdentity> {
  const splash = doc.getElementById('splash')!;
  const form = doc.getElementById('splash-form') as HTMLFormElement;
  const nameInput = doc.getElementById('name') as HTMLInputElement;
  const passcodeInput = doc.getElementById('passcode') as HTMLInputElement;
  const sexGroup = doc.getElementById('sex')!;
  const huesGroup = doc.getElementById('hues')!;
  const characterFields = doc.getElementById('character-fields');
  const enter = doc.getElementById('enter');

  // The name is the first decision on this screen, so make the form ready for
  // typing as soon as it appears instead of requiring an initial click.
  nameInput.focus();

  let sex = SEX_OPTIONS[0]!.value; // default body stays male (UO 400)
  let hue = SKIN_SWATCHES[0]!.hue; // default skin hue stays the historical 1009

  /** The current selections as the creation handshake needs them. */
  const currentIdentity = (): SplashIdentity => {
    // The passcode is intentionally *not* trimmed: it is a secret, and trimming
    // would silently change what the visitor typed. A blank field means "don't
    // reserve" (the server treats '' as anonymous).
    return {
      name: nameInput.value.trim(),
      sex,
      hue,
      appearance: characterFields?.hidden === false,
      passcode: passcodeInput.value,
    };
  };

  const emitChange = (): void => hooks.onChange?.(currentIdentity());

  // Creation mode belongs to the exact credentials the server just checked.
  // Editing either field returns to the credential probe so a completed account
  // never inherits visible appearance controls from the previous name.
  const resetCreation = (): void => {
    if (!characterFields || characterFields.hidden) return;
    characterFields.hidden = true;
    if (enter) enter.textContent = 'Start banksitting!';
    emitChange();
  };
  nameInput.addEventListener('input', resetCreation);
  passcodeInput.addEventListener('input', resetCreation);

  // Sex toggle: one button per body option, the first (male) pre-selected. A
  // segmented two-button control is a more tactile one-of-two pick than a dropdown;
  // clicking selects that body and re-emits the change (the backdrop/preview reads it).
  SEX_OPTIONS.forEach((opt, i) => {
    const btn = makeToggleButton(doc, opt, i === 0);
    btn.addEventListener('click', () => {
      sex = opt.value;
      checkOnly(sexGroup, btn);
      emitChange();
    });
    sexGroup.appendChild(btn);
  });

  // Skin swatches: build one button per skin tone, the first pre-selected. The
  // skin-hue row draws from the skin sub-palette only.
  SKIN_SWATCHES.forEach((swatch, i) => {
    const btn = makeSwatchButton(doc, swatch, i === 0);
    btn.addEventListener('click', () => {
      hue = swatch.hue;
      pressOnly(huesGroup, btn);
      emitChange();
    });
    huesGroup.appendChild(btn);
  });

  emitChange(); // seed the preview with the default bare body

  return new Promise<SplashIdentity>((resolve) => {
    let resolved = false;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      splash.classList.add('hidden');
      const identity = currentIdentity();
      if (!resolved) {
        // First submit: hand control to the host, which opens the connection.
        resolved = true;
        resolve(identity);
      } else {
        // A later submit (the visitor retrying after a rejection): the promise is
        // already settled, so route this attempt through the resubmit hook.
        hooks.onResubmit?.(identity);
      }
    });
  });
}

/** Reveal the one-time appearance step after the server authenticates an incomplete character. */
export function showCharacterCreation(doc: Document): void {
  doc.getElementById('splash')?.classList.remove('hidden');
  const fields = doc.getElementById('character-fields');
  if (fields) fields.hidden = false;
  const enter = doc.getElementById('enter');
  if (enter) enter.textContent = 'Create character';
}

/**
 * Re-show the splash with an error banner (Lightweight Accounts Slice A). Used by
 * `main.ts` when the server **rejects** a login - e.g. the chosen name is already
 * reserved - so the visitor sees why, can edit the name, and resubmit (which fires
 * {@link SplashHooks.onResubmit}). Clears with the next successful entry.
 */
export function setSplashError(doc: Document, message: string): void {
  const splash = doc.getElementById('splash');
  if (splash) splash.classList.remove('hidden');
  const error = doc.getElementById('splash-error');
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}

/** Build one half of the sex toggle: a radio button labelled with the body name. */
function makeToggleButton(doc: Document, opt: SexOption, checked: boolean): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.setAttribute('role', 'radio');
  btn.setAttribute('aria-checked', String(checked));
  btn.textContent = opt.label;
  return btn;
}

/** Check exactly one button in a radiogroup, unchecking its siblings (aria-checked). */
function checkOnly(group: Element, checked: Element): void {
  for (const b of Array.from(group.querySelectorAll('button'))) {
    b.setAttribute('aria-checked', String(b === checked));
  }
}

/** Build one round skin-hue swatch button for the skin-hue row. */
function makeSwatchButton(
  doc: Document,
  swatch: SkinSwatch,
  pressed: boolean,
): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.style.background = swatch.css;
  btn.setAttribute('aria-pressed', String(pressed));
  btn.title = `Hue ${swatch.hue}`;
  return btn;
}

/** Press exactly one button in a group, releasing its siblings (aria-pressed). */
function pressOnly(group: Element, pressed: Element): void {
  for (const b of Array.from(group.querySelectorAll('button'))) {
    b.setAttribute('aria-pressed', String(b === pressed));
  }
}
